import { Capacitor } from "@capacitor/core";

/**
 * Platform Detection Utilities for Panichefs
 * 
 * These utilities help determine the current platform and whether
 * to use Ionic components for the mobile experience.
 */

/**
 * Check if the app is running on a native mobile platform
 * @returns {boolean} true if running on iOS or Android via Capacitor
 */
export const isMobilePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Get the current platform name
 * @returns {string} 'ios', 'android', or 'web'
 */
export const getCurrentPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Check if running on iOS
 * @returns {boolean} true if running on iOS
 */
export const isIOS = () => {
  return getCurrentPlatform() === 'ios';
};

/**
 * Check if running on Android
 * @returns {boolean} true if running on Android
 */
export const isAndroid = () => {
  return getCurrentPlatform() === 'android';
};

/**
 * Check if running on web
 * @returns {boolean} true if running on web
 */
export const isWeb = () => {
  return getCurrentPlatform() === 'web';
};

/**
 * Determine if Ionic components should be used
 * 
 * This function decides whether to render Ionic components or
 * regular web components. Currently, Ionic components are only
 * used when running on native mobile platforms.
 * 
 * @returns {boolean} true if Ionic components should be used
 */
export const shouldUseIonicComponents = () => {
  return isMobilePlatform();
};

/**
 * Get platform-specific class name for styling
 * @returns {string} CSS class name based on platform
 */
export const getPlatformClass = () => {
  const platform = getCurrentPlatform();
  return `platform-${platform}`;
};

/**
 * Execute platform-specific code
 * @param {Object} handlers - Object with ios, android, and web handlers
 * @param {Function} handlers.ios - Handler for iOS
 * @param {Function} handlers.android - Handler for Android
 * @param {Function} handlers.web - Handler for web
 * @returns {any} Result of the appropriate handler
 */
export const executePlatformSpecific = (handlers = {}) => {
  const platform = getCurrentPlatform();
  
  if (platform === 'ios' && handlers.ios) {
    return handlers.ios();
  } else if (platform === 'android' && handlers.android) {
    return handlers.android();
  } else if (platform === 'web' && handlers.web) {
    return handlers.web();
  }
  
  // Default handler if provided
  if (handlers.default) {
    return handlers.default();
  }
  
  return null;
};

/**
 * Get safe area insets for notched devices
 * @returns {Object} Object with top, bottom, left, right insets
 */
export const getSafeAreaInsets = () => {
  // Get computed CSS variables for safe area insets
  const style = getComputedStyle(document.documentElement);
  
  return {
    top: style.getPropertyValue('--ion-safe-area-top') || '0px',
    bottom: style.getPropertyValue('--ion-safe-area-bottom') || '0px',
    left: style.getPropertyValue('--ion-safe-area-left') || '0px',
    right: style.getPropertyValue('--ion-safe-area-right') || '0px'
  };
};

/**
 * Check if device has a notch (iPhone X and later)
 * @returns {boolean} true if device has a notch
 */
export const hasNotch = () => {
  if (!isMobilePlatform()) return false;
  
  const insets = getSafeAreaInsets();
  const topInset = parseInt(insets.top) || 0;
  
  // Devices with notches typically have top inset > 20px
  return topInset > 20;
};

// Platform information object for debugging
export const platformInfo = {
  get platform() {
    return getCurrentPlatform();
  },
  get isNative() {
    return isMobilePlatform();
  },
  get isIOS() {
    return isIOS();
  },
  get isAndroid() {
    return isAndroid();
  },
  get isWeb() {
    return isWeb();
  },
  get shouldUseIonic() {
    return shouldUseIonicComponents();
  },
  get hasNotch() {
    return hasNotch();
  }
};

const platformUtils = {
  isMobilePlatform,
  getCurrentPlatform,
  isIOS,
  isAndroid,
  isWeb,
  shouldUseIonicComponents,
  getPlatformClass,
  executePlatformSpecific,
  getSafeAreaInsets,
  hasNotch,
  platformInfo
};

export default platformUtils;
